import * as i1 from '@angular/common/http';
import { HttpHeaders, HttpParams } from '@angular/common/http';
import * as i0 from '@angular/core';
import { inject, Injectable, NgModule } from '@angular/core';
import * as i2 from '@spartacus/core';
import { LoggerService, normalizeHttpError, USE_CLIENT_TOKEN, InterceptorUtil, provideDefaultConfig } from '@spartacus/core';
import { USER_PROFILE_SERIALIZER, USER_SIGN_UP_SERIALIZER, USER_PROFILE_NORMALIZER, TITLE_NORMALIZER, UserProfileAdapter } from '@spartacus/user/profile/core';
import { catchError, map } from 'rxjs/operators';

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const defaultOccUserProfileConfig = {
  backend: {
    occ: {
      endpoints: {
        userRegister: 'users',
        userForgotPassword: 'forgottenpasswordtokens',
        userResetPassword: 'resetpassword',
        userUpdateLoginId: 'users/${userId}/login',
        userUpdatePassword: 'users/${userId}/password',
        titles: 'titles'
      }
    }
  }
};

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
const CONTENT_TYPE_JSON_HEADER = {
  'Content-Type': 'application/json'
};
const CONTENT_TYPE_URLENCODED_HEADER = {
  'Content-Type': 'application/x-www-form-urlencoded'
};
let OccUserProfileAdapter = /*#__PURE__*/(() => {
  class OccUserProfileAdapter {
    constructor(http, occEndpoints, converter) {
      this.http = http;
      this.occEndpoints = occEndpoints;
      this.converter = converter;
      this.logger = inject(LoggerService);
    }
    update(userId, user) {
      const endpoint = this.occEndpoints.isConfigured('userUpdateProfile') ? 'userUpdateProfile' : 'user';
      const url = this.occEndpoints.buildUrl(endpoint, {
        urlParams: {
          userId
        }
      });
      user = this.converter.convert(user, USER_PROFILE_SERIALIZER);
      return this.http.patch(url, user).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }));
    }
    register(user) {
      const url = this.occEndpoints.buildUrl('userRegister');
      let headers = new HttpHeaders({
        ...CONTENT_TYPE_JSON_HEADER
      });
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
      user = this.converter.convert(user, USER_SIGN_UP_SERIALIZER);
      return this.http.post(url, user, {
        headers
      }).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }), this.converter.pipeable(USER_PROFILE_NORMALIZER));
    }
    registerGuest(guid, password) {
      const url = this.occEndpoints.buildUrl('userRegister');
      let headers = new HttpHeaders({
        ...CONTENT_TYPE_URLENCODED_HEADER
      });
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
      const httpParams = new HttpParams().set('guid', guid).set('password', password);
      return this.http.post(url, httpParams, {
        headers
      }).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }), this.converter.pipeable(USER_PROFILE_NORMALIZER));
    }
    requestForgotPasswordEmail(userEmailAddress) {
      const url = this.occEndpoints.buildUrl('userForgotPassword');
      const httpParams = new HttpParams().set('userId', userEmailAddress);
      let headers = new HttpHeaders({
        ...CONTENT_TYPE_URLENCODED_HEADER
      });
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
      return this.http.post(url, httpParams, {
        headers
      }).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }));
    }
    resetPassword(token, newPassword) {
      const url = this.occEndpoints.buildUrl('userResetPassword');
      let headers = new HttpHeaders({
        ...CONTENT_TYPE_JSON_HEADER
      });
      headers = InterceptorUtil.createHeader(USE_CLIENT_TOKEN, true, headers);
      return this.http.post(url, {
        token,
        newPassword
      }, {
        headers
      }).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }));
    }
    updateEmail(userId, currentPassword, newUserId) {
      const url = this.occEndpoints.buildUrl('userUpdateLoginId', {
        urlParams: {
          userId
        }
      });
      const httpParams = new HttpParams().set('password', currentPassword).set('newLogin', newUserId);
      const headers = new HttpHeaders({
        ...CONTENT_TYPE_URLENCODED_HEADER
      });
      return this.http.put(url, httpParams, {
        headers
      }).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }));
    }
    updatePassword(userId, oldPassword, newPassword) {
      const url = this.occEndpoints.buildUrl('userUpdatePassword', {
        urlParams: {
          userId
        }
      });
      const httpParams = new HttpParams().set('old', oldPassword).set('new', newPassword);
      const headers = new HttpHeaders({
        ...CONTENT_TYPE_URLENCODED_HEADER
      });
      return this.http.put(url, httpParams, {
        headers
      }).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }));
    }
    close(userId) {
      const endpoint = this.occEndpoints.isConfigured('userCloseAccount') ? 'userCloseAccount' : 'user';
      const url = this.occEndpoints.buildUrl(endpoint, {
        urlParams: {
          userId
        }
      });
      return this.http.delete(url).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }));
    }
    loadTitles() {
      const url = this.occEndpoints.buildUrl('titles');
      return this.http.get(url).pipe(catchError(error => {
        throw normalizeHttpError(error, this.logger);
      }), map(titleList => titleList.titles ?? []), this.converter.pipeableMany(TITLE_NORMALIZER));
    }
    static {
      this.ɵfac = function OccUserProfileAdapter_Factory(t) {
        return new (t || OccUserProfileAdapter)(i0.ɵɵinject(i1.HttpClient), i0.ɵɵinject(i2.OccEndpointsService), i0.ɵɵinject(i2.ConverterService));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: OccUserProfileAdapter,
        factory: OccUserProfileAdapter.ɵfac
      });
    }
  }
  return OccUserProfileAdapter;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */
let UserProfileOccModule = /*#__PURE__*/(() => {
  class UserProfileOccModule {
    static {
      this.ɵfac = function UserProfileOccModule_Factory(t) {
        return new (t || UserProfileOccModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: UserProfileOccModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        providers: [provideDefaultConfig(defaultOccUserProfileConfig), {
          provide: UserProfileAdapter,
          useClass: OccUserProfileAdapter
        }]
      });
    }
  }
  return UserProfileOccModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/*
 * SPDX-FileCopyrightText: 2024 SAP Spartacus team <spartacus-team@sap.com>
 *
 * SPDX-License-Identifier: Apache-2.0
 */

/**
 * Generated bundle index. Do not edit.
 */

export { OccUserProfileAdapter, UserProfileOccModule, defaultOccUserProfileConfig };
